// Based on unmaintained https://github.com/aishek/axios-rate-limit
import { AxiosInstance } from 'axios';

type AxiosRateLimiterOptions = {
    maxRequests: number;
    perMilliseconds: number;
};

class AxiosRateLimit {
    private timeslotRequests = 0;
    private queue: { resolve: () => void }[] = [];
    private interceptors: { request: number | null; response: number | null } = { request: null, response: null };
    private timeoutId: NodeJS.Timeout | null = null;

    constructor(
        axios: AxiosInstance,
        private options: AxiosRateLimiterOptions,
    ) {
        this.handleRequest = this.handleRequest.bind(this);
        this.handleResponse = this.handleResponse.bind(this);

        this.enable(axios);
    }

    public enable(axios: AxiosInstance) {
        async function handleRequestError(error: unknown) {
            return Promise.reject(error);
        }

        const handleResponseError = async (error: unknown) => {
            // A failed request occupied a slot just like a successful one, so release it
            // here exactly as handleResponse does. Without this the queued request behind
            // it waits for the timeslot timer instead of starting straight away.
            this.shift();
            return Promise.reject(error);
        };

        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.interceptors.request = axios.interceptors.request.use(this.handleRequest, handleRequestError);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.interceptors.response = axios.interceptors.response.use(this.handleResponse, handleResponseError);
    }

    private async handleRequest(request: any) {
        return new Promise<any>((resolve) => {
            this.push({
                resolve() {
                    resolve(request);
                },
            });
        });
    }

    private handleResponse(response: any) {
        this.shift();
        return response;
    }

    private push(requestHandler: any) {
        if (this.queue.length > 500) {
            throw new Error('Too many requests in queue');
        }
        this.queue.push(requestHandler);
        this.shiftInitial();
    }

    private shiftInitial() {
        setTimeout(() => this.shift(), 0);
    }

    private shift() {
        if (!this.queue.length) {
            return;
        }
        if (this.timeslotRequests === this.options.maxRequests) {
            if (this.timeoutId && typeof this.timeoutId.ref === 'function') {
                this.timeoutId.ref();
            }

            return;
        }

        const queued = this.queue.shift();
        queued?.resolve();

        if (this.timeslotRequests === 0) {
            this.timeoutId = setTimeout(() => {
                this.timeslotRequests = 0;
                this.shift();
            }, this.options.perMilliseconds);

            if (typeof this.timeoutId.unref === 'function') {
                if (this.queue.length === 0) {
                    this.timeoutId.unref();
                }
            }
        }

        this.timeslotRequests += 1;
    }
}

export function axiosRateLimit(axios: AxiosInstance, options: AxiosRateLimiterOptions) {
    new AxiosRateLimit(axios, options);
    return axios;
}
